import axiosConfig from "./axiosConfig";

export type LeadImportSourceKind = "csv" | "xlsx";

export interface CsvImportRequest {
  sourceKind: LeadImportSourceKind;
  sourceFilename: string;
  fileContentBase64: string;
  tags?: string[];
  // Portfolio metadata (optional). When `portfolioTag` is set, the BE
  // upserts a `lead_portfolio_metadata` row at kick-off time and the
  // tag is stamped onto every imported lead. The default org (if set)
  // becomes the bulk-create fallback for any lead whose own
  // landlord/tenant decision is unset. The operator picks ONE role at
  // import time - tenant for HSBC-style portfolios where the org
  // occupies every site; landlord for landlord-portfolio uploads.
  portfolioTag?: string;
  portfolioDisplayName?: string;
  defaultTenantOrgId?: string;
  defaultLandlordOrgId?: string;
  // Two-step preview: the first call (without this flag) returns a
  // cost estimate; the second call (with `confirmEnrichment: true`)
  // actually runs the import. Mirrors /leads/search.
  confirmEnrichment?: boolean;
  // Units the operator declared for area columns whose header stated
  // none, keyed by the column key from
  // `CsvImportPreviewResponse.unitAmbiguousAreaColumns`. Sent on the
  // confirm call. A column left out (or set to "ignore") is not used as
  // the declared area - the BE never guesses between sqm and sqft.
  areaColumnUnits?: Record<string, AreaColumnUnit>;
  // Operator-declared mapping of a source column key onto a canonical key,
  // for headers the BE's automatic matching couldn't recognise. Applied at
  // parse time and wins over automatic matching. Sent on BOTH calls: the
  // preview must be re-run with the mapping to confirm it resolves.
  columnMap?: Record<string, string>;
}

// One column found in the upload, with samples so the operator can tell
// which is which in the mapping step without reopening the spreadsheet.
export interface ImportColumn {
  key: string;
  // The header text as it appeared in the operator's file. This is what makes
  // a mapping reviewable - they recognise "Full Address (as recorded)", not
  // the normalised key we resolved it to.
  label: string;
  rowsPopulated: number;
  sampleValues: string[];
}

export type AreaColumnUnit = "sqm" | "sqft" | "ignore";

// An area column the BE recognised but whose header carries no unit.
// sqm and sqft differ by 10.76x and the BE scales sqft, so rather than
// guess it asks the operator to declare each one at the preview step.
export interface UnitAmbiguousAreaColumn {
  key: string;
  rowsPopulated: number;
  sampleValues: string[];
}

// Preview response shape returned when `confirmEnrichment` is false.
// The FE shows a modal with the cost estimate before re-submitting.
export interface CsvImportPreviewResponse {
  status: "preview";
  rowCount: number;
  // Rows this filename already has leads for. They are skipped at import
  // (`(source, sourceRef)` is UNIQUE), so `estimatedCostUsd` already
  // excludes them and a re-upload is idempotent rather than an error.
  alreadyImportedCount: number;
  estimatedCostUsd: number;
  costPerRowUsd: number;
  unitAmbiguousAreaColumns: UnitAmbiguousAreaColumn[];
  // Required canonical keys the headers didn't resolve. Non-empty means the
  // operator must map them before the import can run - the BE returns a
  // preview rather than rejecting, so the mapping step has something to
  // work with.
  missingRequiredColumns: string[];
  availableColumns: ImportColumn[];
  mappableTargets: string[];
}

export interface CsvImportRowError {
  index: number;
  error: string;
}

// AV-granularity buckets the import-summary alert renders as badges.
export type LeadImportAvBucket =
  | "PREMISE"
  | "SUB_PREMISE"
  | "ROUTE"
  | "OTHER"
  | "NO_VERDICT";

// Result shape the long-running operation writes back into DDB on
// completion. Not the immediate response from POST - the FE polls
// `/operations/<key>?siteId=...` and reads `result` once
// `status === "completed"`.
//
// This is an AGGREGATE summary, not the created leads themselves: the
// per-lead dumps carry heavy polygon geometry that overflows DynamoDB's
// UpdateItem expression-size limit when written to the operation row.
// The summary drives the outcome alert; operators review the actual
// leads on /leads (filtered by the import tag). `createdCount` +
// `avBuckets` + `incompleteCount` are computed BE-side.
//
// `rowsAttempted` / `rowsSkipped` exist because there is no row cap
// on the kick-off endpoint: when the worker hits its soft deadline
// (well inside the 15-min Lambda budget) it stops submitting new
// rows and lets the in-flight set drain. `rowsSkipped > 0` means
// the operator should re-upload the tail (rows starting at index
// `rowsAttempted`).
export interface CsvImportOperationResult {
  createdCount: number;
  avBuckets: Record<LeadImportAvBucket, number>;
  incompleteCount: number;
  // CAPPED: the BE bounds both the number of entries and each message so a
  // run where every row fails can still write its result inside DynamoDB's
  // 400KB item limit. Read `errorCount` for the true total, never
  // `errors.length`.
  errors: CsvImportRowError[];
  errorCount: number;
  tags: string[];
  rowsTotal: number;
  rowsAttempted: number;
  rowsSkipped: number;
  // Rows skipped because a previous import of this filename already
  // created them. Not a failure - re-importing is idempotent.
  rowsAlreadyImported: number;
}

// Immediate response from POST /leads/import/csv: the operation key
// + siteId the FE needs to poll for completion. The actual import
// work happens in the long-running-operation Lambda; the FE polls
// /operations/{key}?siteId=... until status is completed/failed.
export interface CsvImportPendingResponse {
  status: "pending";
  operationKey: string;
  siteId: string;
  rowCount: number;
  tags: string[];
}

export const postLeadImportCsv = async (
  authToken: string,
  body: CsvImportRequest
): Promise<CsvImportPendingResponse | CsvImportPreviewResponse> => {
  const { data } = await axiosConfig.post("/leads/import/csv", body, {
    headers: { Authorization: `Bearer ${authToken}` },
  });
  return data;
};
